import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControl,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  InputAdornment,
  Alert,
  CircularProgress,
  Skeleton,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import { Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import {
  fetchUsers,
  getEmployeeTasksForValidation,
  validateTask,
  getTasksByWeek,
  getTasksByMonth,
  getValidationsByManager,
  getAllTasks,
} from "../api/services";

// ---------------------------------------------------------------
// Shared look (design tokens + small UI helpers) - same as Earnings
// ---------------------------------------------------------------
const ui = {
  primary: "#009db9",
  primaryDark: "#007a8a",
  primarySoft: "#e6f6f9",
  text: "#1f2933",
  muted: "#6b7280",
  border: "#e5e7eb",
  surface: "#f7f9fb",
  success: "#2e7d32",
  successSoft: "#e8f5e9",
  danger: "#d32f2f",
  dangerSoft: "#ffebee",
  warning: "#b26a00",
  warningSoft: "#fff4e5",
  radius: "16px",
  shadow: "0 4px 20px rgba(16, 24, 40, 0.06)",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ui.primary },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ui.primary },
    "&.Mui-disabled": { backgroundColor: "#f3f5f7" },
  },
  "& label.Mui-focused": { color: ui.primary },
};

const primaryButtonSx = {
  backgroundColor: ui.primary,
  color: "#fff",
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  px: 3,
  py: 1.25,
  boxShadow: "none",
  "&:hover": { backgroundColor: ui.primaryDark, boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#d5dbe0", color: "#8a949e" },
};

const secondaryButtonSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "10px",
  color: ui.primary,
  borderColor: ui.primary,
  px: 2.5,
  "&:hover": { borderColor: ui.primaryDark, backgroundColor: ui.primarySoft },
  "&.Mui-disabled": { color: "#b0b7be", borderColor: "#e0e4e8" },
};

const headCellSx = {
  fontWeight: 700,
  color: ui.primaryDark,
  backgroundColor: ui.primarySoft,
  fontSize: "0.8rem",
  py: 1.5,
  borderBottom: `1px solid ${ui.border}`,
};

const bodyCellSx = {
  color: ui.text,
  py: 1.75,
  fontSize: "0.875rem",
  borderBottom: `1px solid ${ui.border}`,
};

const mobileCardSx = {
  border: `1px solid ${ui.border}`,
  borderRadius: "12px",
  p: 2,
  backgroundColor: "#fff",
};

const BackButton = () => (
  <Box sx={{ mt: { xs: 4, sm: 5 }, mb: 2 }}>
    <Button
      component={Link}
      to="/manager-team-metrics"
      startIcon={<ArrowBackIcon fontSize="small" />}
      variant="outlined"
      size="small"
      sx={secondaryButtonSx}
    >
      Back to Team Administration
    </Button>
  </Box>
);

const PageHeader = ({ title }) => (
  <Box sx={{ mb: { xs: 3, md: 4 } }}>
    <Typography
      variant="h4"
      sx={{
        fontWeight: "bold",
        color: ui.text,
        letterSpacing: 0.5,
        fontSize: { xs: "1.5rem", sm: "1.85rem", md: "2.125rem" },
        position: "relative",
        "&::after": {
          content: '""',
          display: "block",
          backgroundColor: ui.primary,
          height: "4px",
          width: { xs: "80px", sm: "100px" },
          position: "absolute",
          left: 0,
          bottom: "-8px",
        },
      }}
    >
      {title}
    </Typography>
  </Box>
);

const SectionCard = ({ title, action, children }) => (
  <Card
    sx={{
      mb: { xs: 2.5, md: 4 },
      borderRadius: ui.radius,
      border: `1px solid ${ui.border}`,
      backgroundColor: "#fff",
      boxShadow: ui.shadow,
      overflow: "visible",
    }}
  >
    <CardContent
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        "&:last-child": { pb: { xs: 2, sm: 3, md: 4 } },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: ui.text,
            pl: 1.5,
            borderLeft: `4px solid ${ui.primary}`,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </CardContent>
  </Card>
);

const StatGrid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
      gap: { xs: 1.5, md: 2 },
      mb: { xs: 2.5, md: 4 },
    }}
  >
    {children}
  </Box>
);

const statTones = {
  primary: { bg: ui.primarySoft, color: ui.primary },
  success: { bg: ui.successSoft, color: ui.success },
  warning: { bg: ui.warningSoft, color: ui.warning },
  danger: { bg: ui.dangerSoft, color: ui.danger },
};

const StatCard = ({ label, value, icon: Icon, tone = "primary" }) => {
  const t = statTones[tone] || statTones.primary;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 2,
        minWidth: 0,
        borderRadius: "14px",
        border: `1px solid ${ui.border}`,
        backgroundColor: "#fff",
        boxShadow: ui.shadow,
      }}
    >
      {Icon && (
        <Box
          sx={{
            flexShrink: 0,
            width: 40,
            height: 40,
            borderRadius: "10px",
            backgroundColor: t.bg,
            color: t.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon fontSize="small" />
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: ui.muted }} noWrap>
          {label}
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            color: ui.text,
            fontSize: { xs: "1.05rem", md: "1.2rem" },
            lineHeight: 1.3,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const chipTones = {
  success: { bg: ui.successSoft, color: ui.success },
  danger: { bg: ui.dangerSoft, color: ui.danger },
  warning: { bg: ui.warningSoft, color: ui.warning },
  info: { bg: ui.primarySoft, color: ui.primaryDark },
  neutral: { bg: "#eef1f4", color: "#4b5563" },
};

const PillChip = ({ label, tone = "neutral" }) => {
  const t = chipTones[tone] || chipTones.neutral;
  return (
    <Chip
      size="small"
      label={label}
      sx={{ fontWeight: 700, borderRadius: "8px", backgroundColor: t.bg, color: t.color }}
    />
  );
};

const EmptyState = ({ icon: Icon = AssignmentOutlinedIcon, title, message }) => (
  <Box sx={{ textAlign: "center", py: { xs: 5, md: 7 }, px: 2 }}>
    <Box
      sx={{
        width: 64,
        height: 64,
        mx: "auto",
        mb: 2,
        borderRadius: "50%",
        backgroundColor: ui.primarySoft,
        color: ui.primary,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ fontSize: 32 }} />
    </Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ui.text }}>
      {title}
    </Typography>
    {message && (
      <Typography variant="body2" sx={{ color: ui.muted, mt: 0.5, maxWidth: 360, mx: "auto" }}>
        {message}
      </Typography>
    )}
  </Box>
);

const ListSkeleton = ({ rows = 5 }) => (
  <Box role="status" aria-label="Loading" sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} variant="rounded" height={56} animation="wave" />
    ))}
  </Box>
);

const PagerBar = ({ currentPage, totalPages, onPrev, onNext }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      mt: 3,
      gap: { xs: 1, sm: 2 },
      flexWrap: "wrap",
    }}
  >
    <Button
      variant="outlined"
      onClick={onPrev}
      disabled={currentPage === 0}
      sx={secondaryButtonSx}
    >
      Previous
    </Button>
    <Typography sx={{ color: ui.text, fontSize: { xs: "0.875rem", sm: "0.95rem" } }}>
      Page {currentPage + 1} of {totalPages}
    </Typography>
    <Button
      variant="outlined"
      onClick={onNext}
      disabled={currentPage >= totalPages - 1}
      sx={secondaryButtonSx}
    >
      Next
    </Button>
  </Box>
);

// Month names for dropdown display
const monthNames = [
  "All Months",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const ValidateMetrics = () => {
  const [tasks, setTasks] = useState([]);
  const [employeeFilter, setEmployeeFilter] = useState("All Employees");
  const [weekFilter, setWeekFilter] = useState("All Weeks");
  const [monthFilter, setMonthFilter] = useState("All Months");
  const [users, setUsers] = useState([]);
  const [employeeMap, setEmployeeMap] = useState({});
  const [weeks, setWeeks] = useState(["All Weeks"]);
  const currentDate = new Date();
  const twoWeeksAgo = new Date(currentDate);
  twoWeeksAgo.setDate(currentDate.getDate() - 14);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [validationType, setValidationType] = useState("on time");
  const [comment, setComment] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  // UI-only loading flag for the validate action
  const [validating, setValidating] = useState(false);

  const pageSize = 10;

  const formatDateForAPI = (date) => date || null;

  // Utility to calculate week number (ISO week)
  const getWeekNumber = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const allTasksResponse = await getAllTasks(currentPage, pageSize);
        console.log('Full response:', allTasksResponse);
        
        // Extract tasks
        let allTasks = [];
        if (allTasksResponse?.data?.content && Array.isArray(allTasksResponse.data.content)) {
          allTasks = allTasksResponse.data.content;
        } else if (Array.isArray(allTasksResponse)) {
          allTasks = allTasksResponse;
        } else if (allTasksResponse?.data && Array.isArray(allTasksResponse.data)) {
          allTasks = allTasksResponse.data;
        } else if (allTasksResponse?.content && Array.isArray(allTasksResponse.content)) {
          allTasks = allTasksResponse.content;
        }
        
        console.log('Extracted tasks:', allTasks);

        const usersData = await fetchUsers();
        const activeUsers = usersData[0]?.activeUsers || [];
        setUsers(activeUsers);
        const userMap = activeUsers.reduce((map, user) => {
          if (user && user.id && user.name) map[user.name] = user.id;
          return map;
        }, {});
        setEmployeeMap(userMap);

        // Fetch validations
        const validationsData = await getValidationsByManager();
        console.log('Validations response:', validationsData);
        
        // Extract validations
        let validations = [];
        if (validationsData?.data?.content && Array.isArray(validationsData.data.content)) {
          validations = validationsData.data.content;
        } else if (Array.isArray(validationsData)) {
          validations = validationsData;
        } else if (validationsData?.data && Array.isArray(validationsData.data)) {
          validations = validationsData.data;
        } else if (validationsData?.content && Array.isArray(validationsData.content)) {
          validations = validationsData.content;
        }
        console.log('Extracted validations:', validations);

        // Map tasks with validation status
        const updatedTasks = allTasks.map((task) => {
          const validation = validations.find((v) => v.taskId === task.id || v.taskId === task.taskId);
          return {
            ...task,
            validationStatus: validation ? (validation.onTime ? "on time" : "delayed") : null,
            comment: validation ? validation.comments : "",
            validatedPoints: validation ? validation.validatedPoints : null,
            validatedBy: validation ? validation.validatedBy : null,
            validationDate: validation ? validation.validationDate : null,
          };
        });
        
        setTasks(updatedTasks);
        setTotalPages(allTasksResponse?.data?.totalPages || 1);

        // Extract weeks from tasks
        const taskWeeks = [...new Set(allTasks.map((task) => task?.week))].filter((week) => week != null);
        setWeeks(["All Weeks", ...taskWeeks.sort((a, b) => a - b).map((week) => `Week ${week}`)]);
      } catch (err) {
        console.error('Error details:', err);
        setError(err.apiErrorDto?.errorMessage || "Failed to fetch initial data");
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [currentPage]);

  const handleGetTasks = async () => {
    setLoading(true);
    try {
      const userId = employeeFilter === "All Employees" ? null : employeeMap[employeeFilter];
      let tasksDataResponse;

      if (startDate && endDate && userId) {
        tasksDataResponse = await getEmployeeTasksForValidation(
          userId,
          formatDateForAPI(startDate),
          formatDateForAPI(endDate)
        );
      } else if (monthFilter !== "All Months" && weekFilter !== "All Weeks" && userId) {
        const monthNum = monthNames.indexOf(monthFilter);
        const weekNum = Number(weekFilter.replace("Week ", ""));
        tasksDataResponse = await getTasksByWeek(userId, monthNum, weekNum);
      } else if (monthFilter !== "All Months" && userId) {
        const monthNum = monthNames.indexOf(monthFilter);
        tasksDataResponse = await getTasksByMonth(userId, currentDate.getFullYear(), monthNum);
      } else {
        tasksDataResponse = await getAllTasks(currentPage, pageSize);
      }

      // Extract tasks
      let tasksData = [];
      if (tasksDataResponse?.data?.content && Array.isArray(tasksDataResponse.data.content)) {
        tasksData = tasksDataResponse.data.content;
      } else if (Array.isArray(tasksDataResponse)) {
        tasksData = tasksDataResponse;
      } else if (tasksDataResponse?.data && Array.isArray(tasksDataResponse.data)) {
        tasksData = tasksDataResponse.data;
      } else if (tasksDataResponse?.content && Array.isArray(tasksDataResponse.content)) {
        tasksData = tasksDataResponse.content;
      }
      
      setTotalPages(tasksDataResponse?.data?.totalPages || 1);

      // Fetch validations
      const validationsData = await getValidationsByManager();
      console.log('Validations response:', validationsData);
      
      // Extract validations
      let validations = [];
      if (validationsData?.data?.content && Array.isArray(validationsData.data.content)) {
        validations = validationsData.data.content;
      } else if (Array.isArray(validationsData)) {
        validations = validationsData;
      } else if (validationsData?.data && Array.isArray(validationsData.data)) {
        validations = validationsData.data;
      } else if (validationsData?.content && Array.isArray(validationsData.content)) {
        validations = validationsData.content;
      }
      console.log('Extracted validations:', validations);

      // Map tasks with validation status
      const updatedTasks = tasksData.map((task) => {
        const validation = validations.find((v) => v.taskId === task.id || v.taskId === task.taskId);
        const employee = task.employee || users.find((u) => u.id === task.user) || { name: "Unknown" };
        const week = task.week != null ? task.week : getWeekNumber(task.createdDate);
        return {
          ...task,
          employee,
          week,
          bonusStatus: task.bonusStatus || (task.completed ? "On Time Bonus" : "Not Completed"),
          validationStatus: validation ? (validation.onTime ? "on time" : "delayed") : null,
          comment: validation ? validation.comments : "",
          validatedPoints: validation ? validation.validatedPoints : null,
          validatedBy: validation ? validation.validatedBy : null,
          validationDate: validation ? validation.validationDate : null,
        };
      });
      setTasks(updatedTasks);

      const taskWeeks = [...new Set(updatedTasks.map((task) => task.week))].filter((week) => week != null);
      setWeeks(["All Weeks", ...taskWeeks.sort((a, b) => a - b).map((week) => `Week ${week}`)]);
    } catch (err) {
      console.error('Error details:', err);
      setError(err.apiErrorDto?.errorMessage || "Failed to fetch tasks");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (task) => {
    setCurrentTask(task);
    setValidationType("on time");
    setComment("");
    setOpenModal(true);
  };

  const handleValidation = async () => {
    setValidating(true);
    try {
      const onTime = validationType === "on time";
      const newStoryPoints = onTime ? currentTask.storyPoints : currentTask.storyPoints / 2;
      const taskData = { taskId: currentTask.id, onTime, comments: comment };
      await validateTask(taskData);
      setOpenModal(false);
      setTasks((prevTasks) =>
        prevTasks.map((t) =>
          t.id === currentTask.id ? { ...t, validationStatus: validationType, actualPoints: newStoryPoints, comment } : t
        )
      );
      console.log(`Task ${currentTask.id} validated as ${validationType}. New story points: ${newStoryPoints}`);
    } catch (err) {
      setError("Failed to validate task");
      console.error(err);
    } finally {
      setValidating(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };

  const filteredTasks = tasks.filter((task) => {
    const employeeMatch = employeeFilter === "All Employees" || task.employee?.name === employeeFilter;
    const weekMatch = weekFilter === "All Weeks" || (task.week != null && weekFilter === `Week ${task.week}`);
    const taskMonth = task.createdDate ? new Date(task.createdDate).getMonth() + 1 : null;
    const monthMatch = monthFilter === "All Months" || (taskMonth && monthNames[taskMonth] === monthFilter);
    return employeeMatch && weekMatch && monthMatch;
  });

  // Presentation-only helpers
  const validatedCount = filteredTasks.filter((t) => t.validationStatus !== null).length;
  const awaitingCount = filteredTasks.filter(
    (t) => t.status === "Done" && t.validationStatus === null
  ).length;

  const dueDateText = (row) => (row.dueDate ? new Date(row.dueDate).toLocaleDateString() : "-");
  const completedDateText = (row) =>
    row.completedDate ? new Date(row.completedDate).toLocaleString() : "-";

  const bonusChip = (row) =>
    row.bonusStatus ? (
      <PillChip
        label={row.bonusStatus}
        tone={row.bonusStatus === "On Time Bonus" ? "success" : "danger"}
      />
    ) : (
      "-"
    );

  const renderAction = (row) => {
    const isValidated = row.validationStatus !== null;
    const showButtons = row.status === "Done" && !isValidated;

    if (showButtons) {
      return (
        <Button
          onClick={() => handleOpenModal(row)}
          variant="outlined"
          size="small"
          startIcon={<FactCheckOutlinedIcon fontSize="small" />}
          sx={{ ...secondaryButtonSx, borderRadius: "8px" }}
        >
          Validate
        </Button>
      );
    }
    if (isValidated) {
      return (
        <Box>
          <PillChip
            label={`Validated as ${row.validationStatus}`}
            tone={row.validationStatus === "on time" ? "success" : "danger"}
          />
          {row.comment && (
            <Typography
              variant="caption"
              sx={{ display: "block", color: ui.muted, mt: 0.5, wordBreak: "break-word" }}
            >
              Comment: {row.comment}
            </Typography>
          )}
        </Box>
      );
    }
    return <PillChip label="Not ready" tone="neutral" />;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Box sx={{ maxWidth: "1200px", mx: "auto", width: "100%", flexGrow: 1, display: "flex", flexDirection: { xs: "column", md: "row" } }}>
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <Sidebar />
        </Box>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            p: { xs: 1, sm: 2, md: 4 },
            pt: { xs: "40px", sm: "50px", md: "120px" },
            pr: { xs: 1, sm: 2, md: 3 },
            backgroundColor: "#fff",
            minHeight: "100vh",
            ml: { md: "20px" },
            position: "relative",
          }}
        >
          <BackButton />

          <PageHeader title="Validate Employee Metrics" />

          {error && (
            <Alert severity="error" variant="filled" sx={{ mb: 2.5, borderRadius: "10px" }}>
              {error}
            </Alert>
          )}

          <StatGrid>
            <StatCard
              label="Tasks shown"
              value={filteredTasks.length.toLocaleString()}
              icon={AssignmentOutlinedIcon}
            />
            <StatCard
              label="Awaiting validation"
              value={awaitingCount.toLocaleString()}
              icon={PendingActionsOutlinedIcon}
              tone="warning"
            />
            <StatCard
              label="Validated"
              value={validatedCount.toLocaleString()}
              icon={TaskAltOutlinedIcon}
              tone="success"
            />
          </StatGrid>

          {/* ------------------------------------------------------ */}
          {/* Filters                                                */}
          {/* ------------------------------------------------------ */}
          <SectionCard title="Filters">
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                },
                gap: 2.5,
              }}
            >
              <Autocomplete
                options={["All Employees", ...users.map(user => user.name)]}
                value={employeeFilter}
                onChange={(event, newValue) => {
                  setEmployeeFilter(newValue || "All Employees");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Employee"
                    sx={fieldSx}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                ListboxProps={{
                  style: {
                    maxHeight: 200,
                    fontSize: '0.9rem'
                  }
                }}
              />
              <Autocomplete
                options={monthNames}
                value={monthFilter}
                onChange={(event, newValue) => {
                  setMonthFilter(newValue || "All Months");
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Filter by Month" sx={fieldSx} />
                )}
                ListboxProps={{
                  style: {
                    maxHeight: 200,
                    fontSize: '0.9rem'
                  }
                }}
              />
              <Autocomplete
                options={weeks}
                value={weekFilter}
                onChange={(event, newValue) => {
                  setWeekFilter(newValue || "All Weeks");
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Filter by Week" sx={fieldSx} />
                )}
                ListboxProps={{
                  style: {
                    maxHeight: 200,
                    fontSize: '0.9rem'
                  }
                }}
              />
              <TextField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                sx={fieldSx}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                sx={fieldSx}
                InputLabelProps={{ shrink: true }}
              />

              <Box
                sx={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: "flex-end",
                  gap: 2,
                  pt: 2.5,
                  borderTop: `1px solid ${ui.border}`,
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleGetTasks}
                  disabled={loading}
                  startIcon={
                    loading ? <CircularProgress size={18} sx={{ color: "inherit" }} /> : null
                  }
                  sx={primaryButtonSx}
                >
                  {loading ? "Loading..." : "Get tasks"}
                </Button>
              </Box>
            </Box>
          </SectionCard>

          {/* ------------------------------------------------------ */}
          {/* Task validation                                        */}
          {/* ------------------------------------------------------ */}
          <SectionCard title="Task validation">
            {loading ? (
              <ListSkeleton rows={5} />
            ) : filteredTasks.length === 0 ? (
              <EmptyState
                title="No tasks found"
                message="Try adjusting the filters and fetching tasks again."
              />
            ) : (
              <>
                {/* Desktop table */}
                <Box
                  sx={{
                    display: { xs: "none", lg: "block" },
                    overflowX: "auto",
                    border: `1px solid ${ui.border}`,
                    borderRadius: "12px",
                  }}
                >
                  <Table sx={{ minWidth: 960 }} aria-label="validation table">
                    <TableHead>
                      <TableRow>
                        {["Employee", "Week", "Jira Ticket", "Ticket Title", "Story Points", "Due Date", "Completed Date", "Automatic Status", "Action"].map((header) => (
                          <TableCell key={header} sx={headCellSx}>
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredTasks.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{ "&:last-child td": { borderBottom: 0 } }}
                        >
                          <TableCell sx={{ ...bodyCellSx, fontWeight: 600, wordBreak: "break-word" }}>
                            {row.employee?.name || "Unknown"}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>{row.week}</TableCell>
                          <TableCell sx={{ ...bodyCellSx, wordBreak: "break-word" }}>
                            <a
                              href={row.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: ui.primaryDark, fontWeight: 600 }}
                            >
                              {row.id}
                            </a>
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, maxWidth: 220, wordBreak: "break-word" }}>
                            {row.title}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>{row.storyPoints}</TableCell>
                          <TableCell sx={bodyCellSx}>{dueDateText(row)}</TableCell>
                          <TableCell sx={bodyCellSx}>{completedDateText(row)}</TableCell>
                          <TableCell sx={bodyCellSx}>{bonusChip(row)}</TableCell>
                          <TableCell sx={bodyCellSx}>{renderAction(row)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>

                {/* Mobile / tablet cards */}
                <Box
                  sx={{
                    display: { xs: "flex", lg: "none" },
                    flexDirection: "column",
                    gap: 1.5,
                  }}
                >
                  {filteredTasks.map((row) => (
                    <Box key={row.id} sx={mobileCardSx}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, color: ui.text, wordBreak: "break-word" }}>
                            {row.employee?.name || "Unknown"}
                          </Typography>
                          <Typography variant="body2" sx={{ color: ui.muted, wordBreak: "break-word" }}>
                            {row.title}
                          </Typography>
                        </Box>
                        {bonusChip(row)}
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "repeat(2, minmax(0, 1fr))",
                            sm: "repeat(3, minmax(0, 1fr))",
                          },
                          gap: 1.5,
                          p: 1.5,
                          mb: 1.5,
                          borderRadius: "10px",
                          backgroundColor: ui.surface,
                        }}
                      >
                        <Box>
                          <Typography variant="caption" sx={{ color: ui.muted }}>
                            Week
                          </Typography>
                          <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{row.week}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: ui.muted }}>
                            Jira ticket
                          </Typography>
                          <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", wordBreak: "break-word" }}>
                            <a
                              href={row.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: ui.primaryDark }}
                            >
                              {row.id}
                            </a>
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: ui.muted }}>
                            Story points
                          </Typography>
                          <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                            {row.storyPoints}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: ui.muted }}>
                            Due date
                          </Typography>
                          <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                            {dueDateText(row)}
                          </Typography>
                        </Box>
                        <Box sx={{ gridColumn: { xs: "1 / -1", sm: "span 2" } }}>
                          <Typography variant="caption" sx={{ color: ui.muted }}>
                            Completed date
                          </Typography>
                          <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                            {completedDateText(row)}
                          </Typography>
                        </Box>
                      </Box>
                      {renderAction(row)}
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {!loading && (
              <PagerBar
                currentPage={currentPage}
                totalPages={totalPages}
                onPrev={() => handlePageChange(currentPage - 1)}
                onNext={() => handlePageChange(currentPage + 1)}
              />
            )}
          </SectionCard>
        </Box>
      </Box>

      {/* ---------------------------------------------------------- */}
      {/* Validate dialog                                            */}
      {/* ---------------------------------------------------------- */}
      <Dialog
        open={openModal}
        onClose={validating ? undefined : () => setOpenModal(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="comment-modal-title"
        PaperProps={{ sx: { borderRadius: "16px", m: { xs: 2, sm: 4 } } }}
      >
        <DialogTitle
          id="comment-modal-title"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            fontWeight: 700,
            color: ui.text,
            pb: 1.5,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: ui.primarySoft,
              color: ui.primary,
            }}
          >
            <FactCheckOutlinedIcon fontSize="small" />
          </Box>
          Validate task
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Box
            sx={{
              p: 2,
              mb: 3,
              borderRadius: "12px",
              backgroundColor: ui.surface,
              border: `1px solid ${ui.border}`,
              display: "grid",
              gap: 1.5,
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ color: ui.muted }}>
                Due date
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {currentTask?.dueDate ? new Date(currentTask.dueDate).toLocaleDateString() : "-"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: ui.muted }}>
                Completed date
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {currentTask?.completedDate ? new Date(currentTask.completedDate).toLocaleString() : "-"}
              </Typography>
            </Box>
          </Box>

          <FormControl component="fieldset" sx={{ mb: 2.5 }}>
            <Typography sx={{ fontWeight: 700, color: ui.text, mb: 0.5 }}>
              Validation status
            </Typography>
            <RadioGroup
              value={validationType}
              onChange={(e) => setValidationType(e.target.value)}
            >
              <FormControlLabel
                value="on time"
                control={<Radio sx={{ "&.Mui-checked": { color: ui.primary } }} />}
                label="On Time"
                disabled={validating}
              />
              <FormControlLabel
                value="delayed"
                control={<Radio sx={{ "&.Mui-checked": { color: ui.primary } }} />}
                label="Delayed"
                disabled={validating}
              />
            </RadioGroup>
          </FormControl>

          <TextField
            multiline
            minRows={3}
            fullWidth
            label="Comment"
            placeholder="Enter your comment here..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={validating}
            sx={fieldSx}
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setOpenModal(false)}
            disabled={validating}
            sx={{ color: ui.muted, textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleValidation}
            disabled={validating}
            startIcon={
              validating ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : null
            }
            sx={{ ...primaryButtonSx, py: 1 }}
          >
            {validating ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ValidateMetrics;